import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Paper, TextField, Button, Grid,
    Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Radio, RadioGroup, FormControlLabel,
    FormLabel, Card, CardContent
} from '@mui/material';
import { Warning as WarningIcon, Send as SendIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import {
    CrisisLogClient,
    LogEntry,
    saveCrisisLog
} from '../services/crisisLog';
import { sendCrisisLogNotification } from '../services/email';

const defaultCrisisLog: CrisisLogClient = {
    incidentCrisisTitle: '',
    dateTimeCrisisDeclared: '',
    dutyOfficerOnCall: '',
    crisisManagementTeamLead: '',
    logEntries: [],
    closureDateTime: '',
    afterActionReviewCompleted: false,
    status: 'active',
    travelFormId: null,
};

const defaultLogEntry: LogEntry = {
    time: '',
    actionTaken: '',
    byWhom: '',
    decisionOutcome: '',
    nextSteps: ''
};

const CrisisLogPage: React.FC = () => {
    const navigate = useNavigate();
    const [log, setLog] = useState<CrisisLogClient>(defaultCrisisLog);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [user, setUser] = useState<any>(null);

    // Load user info and reset form
    useEffect(() => {
        const loadUser = async () => {
            const principal = await getClientPrincipal();
            setUser(principal);
            // Start with a fresh form
            setLog({
                ...defaultCrisisLog,
                dateTimeCrisisDeclared: new Date().toISOString().slice(0, 16)
            });
        };
        loadUser();
    }, []);

    const handleSave = async () => {
        if (!user) {
            setError('User not authenticated');
            return;
        }

        // Validation
        if (!log.incidentCrisisTitle) {
            setError('Please enter the incident/crisis title');
            return;
        }
        if (!log.dateTimeCrisisDeclared) {
            setError('Please enter the date/time crisis was declared');
            return;
        }
        if (!log.dutyOfficerOnCall) {
            setError('Please enter the duty officer on call');
            return;
        }
        if (!log.crisisManagementTeamLead) {
            setError('Please enter the crisis management team lead');
            return;
        }
        if (log.logEntries.length === 0) {
            setError('Please add at least one log entry');
            return;
        }

        // Validate all log entries have required fields
        for (let i = 0; i < log.logEntries.length; i++) {
            const entry = log.logEntries[i];
            if (!entry.time || !entry.actionTaken || !entry.byWhom) {
                setError(`Log entry ${i + 1}: Time, Action Taken, and By Whom are required`);
                return;
            }
        }

        setIsSaving(true);
        setError(null);

        try {
            const updatedLog: CrisisLogClient = {
                ...log,
                status: log.closureDateTime ? 'closed' : 'active'
            };

            const saved = await saveCrisisLog(
                updatedLog,
                user.userId,
                user.userDetails || user.userId,
                user.userDetails || user.userId
            );

            // Send email notification
            try {
                await sendCrisisLogNotification({
                    submitter_name: user.userDetails || user.userId,
                    submitter_email: user.userId,
                    incident_crisis_title: log.incidentCrisisTitle,
                    date_time_crisis_declared: log.dateTimeCrisisDeclared,
                    duty_officer_on_call: log.dutyOfficerOnCall,
                    crisis_management_team_lead: log.crisisManagementTeamLead,
                    log_entries: log.logEntries,
                    closure_date_time: log.closureDateTime,
                    after_action_review_completed: log.afterActionReviewCompleted,
                    status: updatedLog.status
                });
            } catch (emailErr) {
                console.error('Failed to send email notification:', emailErr);
                // Continue even if email fails
            }

            setSnackbarMessage('Crisis log submitted successfully. Redirecting...');
            setSnackbarOpen(true);

            // Redirect to CV search page after 2 seconds
            setTimeout(() => {
                navigate('/');
            }, 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to submit crisis log');
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (field: keyof CrisisLogClient, value: any) => {
        setLog(prev => ({ ...prev, [field]: value }));
    };

    const handleAddLogEntry = () => {
        setLog(prev => ({
            ...prev,
            logEntries: [...prev.logEntries, { ...defaultLogEntry }]
        }));
    };

    const handleRemoveLogEntry = (index: number) => {
        setLog(prev => ({
            ...prev,
            logEntries: prev.logEntries.filter((_, i) => i !== index)
        }));
    };

    const handleLogEntryChange = (index: number, field: keyof LogEntry, value: string) => {
        setLog(prev => ({
            ...prev,
            logEntries: prev.logEntries.map((entry, i) =>
                i === index ? { ...entry, [field]: value } : entry
            )
        }));
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <WarningIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                    <Typography variant="h4" component="h1">
                        Crisis Log Form
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* Crisis Header Information */}
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Crisis Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Incident/Crisis Title"
                                            value={log.incidentCrisisTitle}
                                            onChange={(e) => handleInputChange('incidentCrisisTitle', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Date/Time Crisis Declared"
                                            type="datetime-local"
                                            value={log.dateTimeCrisisDeclared}
                                            onChange={(e) => handleInputChange('dateTimeCrisisDeclared', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Duty Officer on Call"
                                            value={log.dutyOfficerOnCall}
                                            onChange={(e) => handleInputChange('dutyOfficerOnCall', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Crisis Management Team Lead"
                                            value={log.crisisManagementTeamLead}
                                            onChange={(e) => handleInputChange('crisisManagementTeamLead', e.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Log Entries */}
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">
                                        Action Log
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddLogEntry}
                                    >
                                        Add Entry
                                    </Button>
                                </Box>

                                {log.logEntries.length === 0 ? (
                                    <Alert severity="info">
                                        No log entries yet. Click "Add Entry" to create the first entry.
                                    </Alert>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell width="15%">Time</TableCell>
                                                    <TableCell width="20%">Action Taken</TableCell>
                                                    <TableCell width="15%">By Whom</TableCell>
                                                    <TableCell width="20%">Decision/Outcome</TableCell>
                                                    <TableCell width="20%">Next Steps</TableCell>
                                                    <TableCell width="10%">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {log.logEntries.map((entry, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                type="time"
                                                                value={entry.time}
                                                                onChange={(e) => handleLogEntryChange(index, 'time', e.target.value)}
                                                                required
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                multiline
                                                                rows={2}
                                                                value={entry.actionTaken}
                                                                onChange={(e) => handleLogEntryChange(index, 'actionTaken', e.target.value)}
                                                                required
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={entry.byWhom}
                                                                onChange={(e) => handleLogEntryChange(index, 'byWhom', e.target.value)}
                                                                required
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                multiline
                                                                rows={2}
                                                                value={entry.decisionOutcome}
                                                                onChange={(e) => handleLogEntryChange(index, 'decisionOutcome', e.target.value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                multiline
                                                                rows={2}
                                                                value={entry.nextSteps}
                                                                onChange={(e) => handleLogEntryChange(index, 'nextSteps', e.target.value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleRemoveLogEntry(index)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Closure Information */}
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Closure Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Closure Date/Time"
                                            type="datetime-local"
                                            value={log.closureDateTime}
                                            onChange={(e) => handleInputChange('closureDateTime', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            helperText="Leave empty if crisis is still active"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormLabel component="legend">After-Action Review Completed?</FormLabel>
                                        <RadioGroup
                                            row
                                            value={log.afterActionReviewCompleted ? 'yes' : 'no'}
                                            onChange={(e) => handleInputChange('afterActionReviewCompleted', e.target.value === 'yes')}
                                        >
                                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                                            <FormControlLabel value="no" control={<Radio />} label="No" />
                                        </RadioGroup>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/')}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SendIcon />}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Submitting...' : 'Submit Crisis Log'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </Container>
    );
};

export default CrisisLogPage;
